import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  UseCreateVqaInferenceRequest,
  VqaCreatePromptResponse,
  VqaInferenceResponse,
  VqaPromptBase,
  VqaPromptsList,
} from '@sparkai/shared-types'
import { useToast } from '@sparkai/ui'

import { apiClient } from '../../apiClient'

const VQA_PROMPT_QUERY_PREFIX = 'vqa-prompts'

function useCreateVqaInference(programId: string, onSuccess: (data: VqaInferenceResponse) => void) {
  const toast = useToast()
  return useMutation({
    mutationFn: async (formData: UseCreateVqaInferenceRequest): Promise<VqaInferenceResponse> => {
      const body = {
        value: formData.prompt,
        imageUrl: formData.imageUrl,
      }
      const { data } = await apiClient.post(`/int/v3/programs/${programId}/insights/vqa/inference`, body)
      return data
    },
    onSuccess,
    onError: (error) => {
      console.error(error)
      toast({
        type: 'error',
        title: 'Error creating VQA inference',
      })
    },
  })
}

function useGetVqaPromptsForProgram(programId?: string) {
  return useQuery({
    queryKey: [VQA_PROMPT_QUERY_PREFIX, programId],
    queryFn: async () => {
      const response = await apiClient.get<VqaPromptsList>(
        `/int/v3/programs/${programId}/insights/vqa/prompts`
      )
      return response.data.prompts
    },
    enabled: Boolean(programId),
  })
}

function useCreateVqaPrompt(programId: string) {
  const queryClient = useQueryClient()
  const toast = useToast()

  return useMutation({
    mutationFn: async (formData: VqaPromptBase): Promise<VqaCreatePromptResponse> => {
      const body = {
        value: formData.prompt,
      }
      const { data } = await apiClient.post(`/int/v3/programs/${programId}/insights/vqa/prompts`, body)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [VQA_PROMPT_QUERY_PREFIX, programId] })
    },
    onError: (error) => {
      console.error(error)
      toast({
        type: 'error',
        title: 'Error creating VQA prompt',
      })
    },
  })
}

export { useCreateVqaInference, useCreateVqaPrompt, useGetVqaPromptsForProgram }
